import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface StickyPageHeaderProps {
  /** Title of the page */
  title: string;
  /** Subtitle or description */
  subtitle?: string;
  /** Back navigation path */
  backTo?: string;
  /** Back button label */
  backLabel?: string;
  /** Action buttons to display on the right */
  actions?: React.ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the back button */
  showBackButton?: boolean;
}

/**
 * StickyPageHeader Component
 * 
 * A reusable sticky header that aligns perfectly with the top navigation.
 * Provides consistent spacing and positioning across all table/form pages.
 * 
 * @param props - Component props
 * @returns React component
 */
export const StickyPageHeader: React.FC<StickyPageHeaderProps> = ({
  title,
  subtitle,
  backTo = '/',
  backLabel = 'Back',
  actions,
  className = '',
  showBackButton = true,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(backTo);
  };

  return (
    <div 
      className={`sticky left-0 right-0 bg-white border-b shadow-sm ${className}`}
      style={{ 
        top: '64px', // Exactly 64px to align with TopNav h-16
        zIndex: 41   // Higher than TopNav z-20 but safe for modals
      }}
    >
      <div className="w-full px-4 md:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Left side - Back button and title */}
          <div className="flex items-center gap-4">
            {showBackButton && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleBack}
                className="flex items-center gap-1 text-neutral-600 hover:text-neutral-900"
              >
                <ArrowLeft size={16} />
                <span>{backLabel}</span>
              </Button>
            )}
            
            <div>
              <h1 className="text-xl font-bold text-neutral-900">
                {title}
              </h1>
              {subtitle && (
                <p className="text-sm text-neutral-600 mt-1">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          
          {/* Right side - Action buttons */}
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 